import { ChangeDetectorRef, Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { environment } from 'environments/environment.prod';
import * as feather from 'feather-icons';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { French } from 'flatpickr/dist/l10n/fr.js';
import { HeaderService } from '../../../../services/header/header.service';
import { FournisseurService } from '../../../../services/Fournisseur/fournisseur.service';
import { Fournisseur, Telephone, Address, Email } from '../../../../models/fournisseur';
import {
    CoordinateParamsPayload,
    ParamTablesService,
    parseCoordinateValeurFromResponse
} from '../../../../services/parametrage/param-tables.service';

type ParamDataFieldType = 'varchar' | 'date' | 'nombre' | 'select';

interface ParamDataRow {
    libelle: string;
    type: ParamDataFieldType;
    valeur: string;
    champ_db: string;
    param_table_id?: number | null;
}

interface ParamDataSelectOption {
    label: string;
    value: string;
}

interface ApiResponse {
    message?: string;
    fournisseur?: Fournisseur;
    id?: number;
}

@Component({
    selector: 'app-add-fournisseur',
    templateUrl: './add-fournisseur.component.html',
    styleUrls: ['./add-fournisseur.component.scss']
})
export class AddFournisseurComponent implements OnInit, AfterViewInit {
    steps: string[] = ['Informations du fournisseur', 'Coordonnées', 'Paramètres des données', 'Terminé'];
    currentStep: number = 0;
    fournisseur: Fournisseur = {
        civilite: '',
        nom_entreprise: '',
        numero_identification_fiscale: '',
        site_web: '',
        station_id: 0,
        user_id: 0,
        identifiant_unique: '',
        is_archive: false,
        emails: [{ email: '', user_id: 0, fournisseur_id: 0, station_id: 0 }],
        telephones: [],
        adresses: []
    };
    submittedStep1 = false;
    submittedStep2 = false;
    addressArray: Address[] = [];
    isStep1Valid: boolean = false;
    isStep2Valid: boolean = false;
    isStep3Valid: boolean = true;
    /** Étape « Paramètres des données » (après coordonnées). */
    isStepParamDataValid = true;
    readonly tableDbFournisseur = 'fournisseurs';
    paramTableRows: ParamDataRow[] = [];
    paramTablesLoading = false;
    private readonly paramSelectOptionsCache = new Map<number, ParamDataSelectOption[]>();
    mode: 'add' | 'edit' = 'add';
    file: string = environment.file_url;
    private options: GlobalConfig;
    isSaved: boolean = false;
    originalFournisseurId: number | null = null;
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    hasAddedPhone: boolean = false;
    hasAddedAddress: boolean = false;
    fournisseurId: number | null = null;
    currentUserId: number = 0;
    currentStationId: number = 0;
    touchedPhoneRows = new Set<number>();
    touchedAddressRows = new Set<number>();
    @ViewChild('step1Form') step1Form!: NgForm;
    @ViewChild('step2Form') step2Form!: NgForm;
    @ViewChild('step3Form') step3Form!: NgForm;

    constructor(
        private cd: ChangeDetectorRef,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private headerService: HeaderService,
        private fournisseurService: FournisseurService,
        private paramTablesService: ParamTablesService
    ) {
        this.options = this.toastr.toastrConfig;
    }

    ngOnInit(): void {
        this.loadDynamicIds();

        const navState = (this.router.getCurrentNavigation()?.extras?.state
            ?? (typeof history !== 'undefined' ? history.state : null)) as
            | { fournisseur?: Fournisseur; isEditMode?: boolean }
            | null
            | undefined;

        if (navState?.fournisseur && navState.isEditMode) {
            this.mode = 'edit';
            const fournisseurData = navState.fournisseur as Fournisseur;
            this.fournisseurId = fournisseurData.id || null;
            this.originalFournisseurId = this.fournisseurId;
            if (this.fournisseurId && !isNaN(this.fournisseurId)) {
                this.loadFournisseurData(this.fournisseurId);
            } else {
                this.populateForm(fournisseurData);
                this.toastr.warning('ID de fournisseur invalide dans les données de navigation. Formulaire pré-rempli sans chargement API.', 'Attention');
            }
            this.currentStep = 0;
        } else if (this.isEditRoute()) {
            this.mode = 'edit';
            const fournisseurId = this.resolveFournisseurIdFromRoute();
            if (fournisseurId && !isNaN(+fournisseurId)) {
                this.fournisseurId = +fournisseurId;
                this.originalFournisseurId = this.fournisseurId;
                this.loadFournisseurData(this.fournisseurId);
            } else {
                this.toastr.error('Aucun ID de fournisseur valide fourni pour la modification.', 'Erreur');
                this.router.navigate(['/bibliotheque-fournisseurs/actif']);
            }
            this.currentStep = 0;
        } else {
            this.mode = 'add';
            this.fournisseur.emails = [{
                email: '',
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0,
                station_id: this.currentStationId
            }];
            this.fournisseur.telephones = [{
                type: null,
                num_tel: null,
                code_pays: '',
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0
            }];
            this.addressArray = [{
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0
            }];
            this.hasAddedPhone = true;
            this.hasAddedAddress = true;
        }

        this.headerService.setConfig({
            title: this.mode === 'edit' ? 'Modifier Fournisseur' : 'Ajouter Fournisseur',
            buttons: []
        });

        this.validateStep2();
        this.validateStep3();
        this.cd.markForCheck();
        this.refreshFeatherIcons();
    }

    private resolveFournisseurIdFromRoute(): string | null {
        let route: ActivatedRoute | null = this.route;
        while (route) {
            const id = route.snapshot.paramMap.get('id');
            if (id) {
                return id;
            }
            route = route.parent;
        }
        return null;
    }

    private isEditRoute(): boolean {
        let route: ActivatedRoute | null = this.route;
        while (route) {
            if (route.snapshot.url.some((segment) => segment.path === 'edit')) {
                return true;
            }
            route = route.parent;
        }
        return !!this.resolveFournisseurIdFromRoute();
    }

    ngAfterViewInit(): void {
        this.refreshFeatherIcons();
    }

    private loadDynamicIds(): void {
        try {
            const user = localStorage.getItem('currentUser');
            if (user) {
                const currentUser = JSON.parse(user);
                this.currentUserId = currentUser?.id || 5;
                this.currentStationId = currentUser?.stations?.[0]?.id || 2;
                this.fournisseur.user_id = this.currentUserId;
                this.fournisseur.station_id = this.currentStationId;
                this.addressArray = [];
                this.fournisseur.emails = [{
                    email: '',
                    user_id: this.currentUserId,
                    fournisseur_id: this.fournisseurId || 0,
                    station_id: this.currentStationId
                }];
            } else {
                this.currentUserId = 5;
                this.currentStationId = 2;
                this.fournisseur.user_id = this.currentUserId;
                this.fournisseur.station_id = this.currentStationId;
                this.addressArray = [];
                this.fournisseur.emails = [{
                    email: '',
                    user_id: this.currentUserId,
                    fournisseur_id: this.fournisseurId || 0,
                    station_id: this.currentStationId
                }];
                this.toastr.warning('Utilisateur non trouvé dans localStorage. Utilisation des IDs par défaut.', 'Attention');
            }
        } catch (error) {
            this.currentUserId = 5;
            this.currentStationId = 2;
            this.fournisseur.user_id = this.currentUserId;
            this.fournisseur.station_id = this.currentStationId;
            this.addressArray = [];
            this.fournisseur.emails = [{
                email: '',
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0,
                station_id: this.currentStationId
            }];
            this.toastr.error('Erreur lors de la récupération des IDs utilisateur/station.', 'Erreur');
        }
    }

    private loadFournisseurData(fournisseurId: number): void {
        if (!fournisseurId || isNaN(fournisseurId)) {
            this.toastr.error('ID de fournisseur invalide.', 'Erreur');
            this.isLoading = false;
            this.router.navigate(['/bibliotheque-fournisseurs/actif']);
            this.cd.detectChanges();
            return;
        }
        this.isLoading = true;
        this.fournisseurService.getFournisseurById(fournisseurId).subscribe({
            next: (fournisseurData) => {
                this.populateForm(fournisseurData);
                this.isLoading = false;
                this.cd.detectChanges();
            },
            error: (error) => {
                this.isLoading = false;
                this.toastr.error('Erreur lors du chargement des données du fournisseur.', 'Erreur');
                this.router.navigate(['/bibliotheque-fournisseurs/actif']);
                this.cd.detectChanges();
            }
        });
    }

    trackByIndex(index: number): number {
        return index;
    }

    populateForm(fournisseurData: Fournisseur): void {
        let emails: Email[] = [];
        if (fournisseurData.emails) {
            if (Array.isArray(fournisseurData.emails) && typeof fournisseurData.emails[0] === 'string') {
                emails = fournisseurData.emails.map(email => ({
                    email: email as string,
                    user_id: this.currentUserId,
                    fournisseur_id: fournisseurData.id || this.fournisseurId || 0,
                    station_id: this.currentStationId,
                    id: undefined,
                    created_at: undefined,
                    updated_at: undefined
                } as Email));
            } else {
                emails = (fournisseurData.emails as Email[]).map(email => ({
                    id: email.id || undefined,
                    email: email.email || '',
                    user_id: this.currentUserId,
                    fournisseur_id: fournisseurData.id || this.fournisseurId || 0,
                    station_id: this.currentStationId,
                    created_at: email.created_at || undefined,
                    updated_at: email.updated_at || undefined
                } as Email));
            }
        }
        if (emails.length === 0) {
            emails = [{ email: '', user_id: this.currentUserId, fournisseur_id: fournisseurData.id || this.fournisseurId || 0, station_id: this.currentStationId }];
        }

        let telephones: Telephone[] = [];
        if (fournisseurData.telephones?.length > 0) {
            telephones = fournisseurData.telephones.map(tel => ({
                id: tel.id || undefined,
                type: tel.type || null,
                num_tel: tel.num_tel ? String(tel.num_tel) : null,
                code_pays: tel.code_pays || null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: fournisseurData.id || this.fournisseurId || 0,
                created_at: tel.created_at || undefined,
                updated_at: tel.updated_at || undefined
            } as Telephone));
        } else {
            telephones = [{
                type: null,
                num_tel: null,
                code_pays: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: fournisseurData.id || this.fournisseurId || 0
            }];
        }

        let addresses: Address[] = [];
        if (fournisseurData.adresses?.length > 0) {
            addresses = fournisseurData.adresses.map(addr => ({
                id: addr.id || undefined,
                type: addr.type ? (addr.type === 'adresse_fac' ? 'facturation' : addr.type === 'adresse_li' ? 'livraison' : addr.type) : null,
                pays: addr.pays ? (addr.pays === 'FR' ? 'France' : addr.pays === 'BE' ? 'Belgique' : addr.pays === 'CH' ? 'Suisse' : addr.pays === 'MA' ? 'Maroc' : addr.pays) : null,
                region: addr.region || null,
                code_postal: addr.code_postal || null,
                adresse: addr.adresse || null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: fournisseurData.id || this.fournisseurId || 0,
                created_at: addr.created_at || undefined,
                updated_at: addr.updated_at || undefined
            } as Address));
        } else {
            addresses = [{
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: fournisseurData.id || this.fournisseurId || 0
            }];
        }

        const civilite = fournisseurData.civilite || '';
        const civiliteNorm = civilite === 'avant' ? 'Homme' : civilite === 'apres' ? 'Femme' : civilite;
        this.fournisseur = {
            id: fournisseurData.id || undefined,
            civilite: civiliteNorm,
            nom_entreprise: fournisseurData.nom_entreprise || '',
            numero_identification_fiscale: fournisseurData.numero_identification_fiscale || '',
            site_web: fournisseurData.site_web || '',
            station_id: fournisseurData.station_id || this.currentStationId,
            user_id: fournisseurData.user_id || this.currentUserId,
            identifiant_unique: fournisseurData.identifiant_unique || '',
            emails: emails,
            is_archive: fournisseurData.is_archive || false,
            created_at: fournisseurData.created_at || undefined,
            updated_at: fournisseurData.updated_at || undefined,
            telephones: telephones,
            adresses: addresses
        };
        this.fournisseurId = fournisseurData.id || null;
        this.addressArray = this.fournisseur.adresses;
        this.hasAddedPhone = this.fournisseur.telephones.length > 0;
        this.hasAddedAddress = this.addressArray.length > 0;
        this.validateStep1();
        this.validateStep2();
        this.validateStep3();
        this.cd.markForCheck();
    }

    refreshFeatherIcons(): void {
        feather.replace();
        this.cd.detectChanges();
    }

    addEmail(): void {
        if (this.fournisseur.emails!.length < 5) {
            this.fournisseur.emails = [...this.fournisseur.emails!, {
                email: '',
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0,
                station_id: this.currentStationId
            } as Email];
            this.validateStep2();
            this.refreshFeatherIcons();
        } else {
            this.toasterInfo('Vous ne pouvez pas ajouter plus de 5 emails.');
        }
    }

    deleteEmail(index: number): void {
        if (this.fournisseur.emails!.length > 1) {
            this.fournisseur.emails = this.fournisseur.emails!.filter((_, i) => i !== index);
            this.validateStep2();
            this.refreshFeatherIcons();
        } else {
            this.fournisseur.emails = [{ email: '', user_id: this.currentUserId, fournisseur_id: this.fournisseurId || 0, station_id: this.currentStationId }];
            this.toasterInfo('Vous devez conserver au moins un email.');
        }
    }

    onTimelineStepSelected(stepIndex: number): void {
        if (stepIndex >= 0 && stepIndex < this.steps.length && this.isPreviousStepsValid(stepIndex)) {
            this.currentStep = stepIndex;
            if (stepIndex === 2) {
                this.loadFournisseurParamTables();
            }
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        }
    }

    isPreviousStepsValid(stepIndex: number): boolean {
        for (let i = 0; i < stepIndex; i++) {
            switch (i) {
                case 0:
                    if (!this.isStep1Valid) return false;
                    break;
                case 1:
                    if (!this.isStep2Valid) return false;
                    break;
                case 2:
                    if (!this.isStepParamDataValid) return false;
                    break;
            }
        }
        return true;
    }

    nextStep(): void {
        if (this.isCurrentStepValid()) {
            this.currentStep++;
            if (this.currentStep === 2) {
                this.loadFournisseurParamTables();
            }
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        }
    }

    goBack(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
            this.cd.detectChanges();
        } else {
            this.router.navigate(['/bibliotheque-fournisseurs/actif']);
        }
    }

    retour(): void {
        this.router.navigate(['/bibliotheque-fournisseurs/actif']);
    }

    isCurrentStepValid(): boolean {
        switch (this.currentStep) {
            case 0:
                return this.isStep1Valid;
            case 1:
                return this.isStep2Valid;
            case 2:
                return this.isStepParamDataValid;
            case 3:
                return true;
            default:
                return false;
        }
    }

    validateStep1(): void {
        this.isStep1Valid = !!this.fournisseur.nom_entreprise &&
            !!this.fournisseur.numero_identification_fiscale &&
            !!this.fournisseur.civilite &&
            !!this.fournisseur.identifiant_unique;
        this.cd.markForCheck();
    }

    validateStep2(): void {
        let hasError = false;

        this.fournisseur.telephones.forEach((phone, i) => {
            const isTouched = this.hasAnyPhoneFieldFilled(i);
            if (isTouched) {
                const valid = !!phone.type &&
                    !!phone.code_pays?.trim() &&
                    phone.num_tel &&
                    /^[1-9][0-9]{7}$/.test(phone.num_tel.toString().trim());
                if (!valid) hasError = true;
            }
        });

        this.addressArray.forEach(addr => {
            if (addr.type) {
                if (!addr.pays || !addr.region || !addr.code_postal || !addr.adresse) {
                    hasError = true;
                }
            }
        });

        this.isStep2Valid = !hasError;
        this.cd.markForCheck();
    }

    resetStep2Errors(): void {
        this.step2Errors = { phones: {}, addresses: {} };
    }

    validateStep3(): boolean {
        this.cd.markForCheck();
        return this.isStep3Valid;
    }

    /**
     * Identiques à add-product (`stationId` / `clientId` utilisés pour `getParamTablesByTable`) :
     * GET `param-tables/by-table/:tableDb?station_id=&user_id=`.
     */
    private getParamTablesApiIds(): { stationId: number; userId: number } {
        let currentUser: any = null;
        try {
            const raw = localStorage.getItem('currentUser');
            if (raw) {
                currentUser = JSON.parse(raw);
            }
        } catch {
            currentUser = null;
        }
        const stationId = Number(
            currentUser?.stations?.[0]?.station_id ??
                currentUser?.stations?.[0]?.id ??
                currentUser?.station_id ??
                this.currentStationId ??
                0
        );
        const userId = Number(currentUser?.id ?? this.currentUserId ?? 0);
        return { stationId, userId };
    }

    loadFournisseurParamTables(): void {
        const { stationId: sid, userId: uid } = this.getParamTablesApiIds();
        if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(uid) || uid <= 0) {
            this.toastr.warning('Station ou utilisateur introuvable pour charger les paramètres.', 'Attention');
            this.paramTableRows = [this.createEmptyParamRow(0)];
            return;
        }
        this.paramSelectOptionsCache.clear();
        this.paramTablesLoading = true;
        this.paramTablesService.getParamTablesByTable(this.tableDbFournisseur, sid, uid).pipe(
            switchMap((raw) => {
                let rows = this.normalizeParamTableResponse(raw);
                if (rows.length === 0) {
                    rows = [this.createEmptyParamRow(0)];
                }
                const fid = this.resolveFournisseurIdForCoordinateValeur();
                return this.applyCoordinateValeursFromApi$(rows, sid, uid, fid).pipe(
                    map((withVals) => (withVals.length === 0 ? [this.createEmptyParamRow(0)] : withVals)),
                    switchMap((finalRows) => this.loadSelectParamOptionsForRows$(finalRows, sid, uid))
                );
            }),
            finalize(() => {
                this.paramTablesLoading = false;
                this.cd.detectChanges();
            })
        ).subscribe({
            next: (rows) => {
                this.paramTableRows = rows;
                this.validateParamStep();
            },
            error: () => {
                this.toastr.error('Impossible de charger les paramètres des données.');
                this.paramTableRows = [this.createEmptyParamRow(0)];
            }
        });
    }

    private resolveFournisseurIdForCoordinateValeur(): number | null {
        if (this.fournisseurId != null && Number.isFinite(this.fournisseurId) && this.fournisseurId > 0) {
            return Math.floor(this.fournisseurId);
        }
        const fid = this.fournisseur?.id;
        if (fid != null && Number.isFinite(Number(fid)) && Number(fid) > 0) {
            return Math.floor(Number(fid));
        }
        return null;
    }

    private applyCoordinateValeursFromApi$(
        rows: ParamDataRow[],
        stationId: number,
        userId: number,
        fournisseurId: number | null
    ): Observable<ParamDataRow[]> {
        const tableIds = [
            ...new Set(
                rows
                    .map((r) => r.param_table_id)
                    .filter((id): id is number => id != null && id > 0)
            )
        ];
        if (tableIds.length === 0 || fournisseurId == null || fournisseurId <= 0) {
            return of(rows);
        }
        return forkJoin(
            tableIds.map((ptid) =>
                this.paramTablesService
                    .getCoordinateParamsByParamTableFournisseur(ptid, fournisseurId, stationId, userId)
                    .pipe(catchError(() => of(null)))
            )
        ).pipe(
            map((responses) => {
                const byParamTableId = new Map<number, string>();
                tableIds.forEach((ptid, idx) => {
                    const raw = responses[idx];
                    byParamTableId.set(ptid, parseCoordinateValeurFromResponse(raw, fournisseurId, 'fournisseur_id'));
                });
                return rows.map((r) => {
                    if (r.param_table_id == null || r.param_table_id <= 0) {
                        return r;
                    }
                    const v = byParamTableId.get(r.param_table_id);
                    return v !== undefined ? { ...r, valeur: v } : r;
                });
            })
        );
    }

    getParamSelectOptions(paramTableId: number | null | undefined): ParamDataSelectOption[] {
        if (paramTableId == null || !Number.isFinite(Number(paramTableId)) || Number(paramTableId) <= 0) {
            return [];
        }
        return this.paramSelectOptionsCache.get(Math.floor(Number(paramTableId))) ?? [];
    }

    private loadSelectParamOptionsForRows$(
        rows: ParamDataRow[],
        stationId: number,
        userId: number
    ): Observable<ParamDataRow[]> {
        const ids = [
            ...new Set(
                rows
                    .filter((r) => r.type === 'select' && r.param_table_id != null && r.param_table_id > 0)
                    .map((r) => Math.floor(r.param_table_id as number))
            )
        ];
        if (ids.length === 0) {
            return of(rows);
        }
        return forkJoin(
            ids.map((ptid) =>
                this.paramTablesService.getSelectParamsByParamTable(ptid, stationId, userId).pipe(
                    map((raw) => ({ ptid, opts: this.parseSelectParamsResponse(raw) })),
                    catchError(() => of({ ptid, opts: [] as ParamDataSelectOption[] }))
                )
            )
        ).pipe(
            tap((pairs) => {
                pairs.forEach(({ ptid, opts }) => this.paramSelectOptionsCache.set(ptid, opts));
            }),
            map(() => rows)
        );
    }

    private parseSelectParamsResponse(raw: unknown): ParamDataSelectOption[] {
        const list = this.unwrapSelectParamsList(raw);
        const out: ParamDataSelectOption[] = [];
        for (const item of list) {
            if (!item || typeof item !== 'object') {
                continue;
            }
            const o = item as Record<string, unknown>;
            const lib = String(o['libelle'] ?? o['label'] ?? o['nom'] ?? '').trim();
            const valueRaw = o['value'] ?? o['valeur'] ?? o['code'] ?? o['id'] ?? o['select_param_id'];
            const val = valueRaw != null && valueRaw !== '' ? String(valueRaw).trim() : '';
            const label = lib || val;
            const value = val || lib;
            if (label === '' && value === '') {
                continue;
            }
            out.push({ label: label || value, value: value || label });
        }
        return out;
    }

    private unwrapSelectParamsList(raw: unknown): unknown[] {
        if (Array.isArray(raw)) {
            return raw;
        }
        if (raw && typeof raw === 'object') {
            const o = raw as Record<string, unknown>;
            const cand =
                o['data'] ??
                o['rows'] ??
                o['items'] ??
                o['options'] ??
                o['select_params'] ??
                o['results'];
            if (Array.isArray(cand)) {
                return cand;
            }
            if (cand && typeof cand === 'object' && !Array.isArray(cand)) {
                const inner = (cand as Record<string, unknown>)['rows'] ?? (cand as Record<string, unknown>)['data'];
                if (Array.isArray(inner)) {
                    return inner;
                }
            }
        }
        return [];
    }

    private createEmptyParamRow(index: number): ParamDataRow {
        return {
            libelle: '',
            type: 'varchar',
            valeur: '',
            champ_db: `param${index + 1}`,
            param_table_id: null
        };
    }

    private normalizeParamTableResponse(raw: unknown): ParamDataRow[] {
        const anyRaw = raw as Record<string, unknown> | unknown[] | null | undefined;
        let list: unknown[] = [];
        if (Array.isArray(anyRaw)) {
            list = anyRaw;
        } else if (anyRaw && typeof anyRaw === 'object') {
            const o = anyRaw as Record<string, unknown>;
            const cand =
                o['data'] ??
                o['rows'] ??
                o['param_tables'] ??
                o['parametrage'] ??
                o['items'];
            list = Array.isArray(cand) ? cand : [];
        }
        const rows: ParamDataRow[] = [];
        for (let i = 0; i < list.length; i++) {
            const row = list[i] as Record<string, unknown>;
            if (!row || typeof row !== 'object') {
                continue;
            }
            const lib =
                (row['libelle_champ'] ?? row['libelle'] ?? row['label'] ?? '') as string;
            const typeRaw = (row['type'] ??
                row['type_champ'] ??
                row['champ_type'] ??
                row['param_type'] ??
                row['data_type'] ??
                'varchar') as string;
            const typeNorm = this.normalizeParamType(typeRaw);
            const champ =
                (row['champ_db'] ?? row['champDb'] ?? `param${i + 1}`) as string;
            const rawPid =
                row['id'] ?? row['param_table_id'] ?? row['paramTableId'] ?? row['parametrage_table_id'];
            let param_table_id: number | null = null;
            if (rawPid != null && rawPid !== '') {
                const n = Number(rawPid);
                if (Number.isFinite(n) && n > 0) {
                    param_table_id = Math.floor(n);
                }
            }
            rows.push({
                libelle: lib != null ? String(lib) : '',
                type: typeNorm,
                valeur: '',
                champ_db: champ != null ? String(champ) : `param${i + 1}`,
                param_table_id
            });
        }
        return rows;
    }

    private normalizeParamType(t: string): ParamDataFieldType {
        const s = (t || '').toLowerCase().trim();
        if (s === 'date' || s === 'datetime') {
            return 'date';
        }
        if (
            s === 'nombre' ||
            s === 'number' ||
            s === 'double' ||
            s === 'numeric' ||
            s === 'int' ||
            s === 'integer' ||
            s === 'float' ||
            s === 'decimal'
        ) {
            return 'nombre';
        }
        if (s === 'select' || s === 'liste' || s === 'dropdown' || s === 'list') {
            return 'select';
        }
        return 'varchar';
    }

    validateParamStep(): void {
        this.isStepParamDataValid = true;
    }

    readonly paramDateFlatpickrOptions: FlatpickrOptions = {
        locale: French,
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        allowInput: true,
        disableMobile: true,
        monthSelectorType: 'dropdown',
        altInputClass: 'form-control',
        onReady: (_selectedDates, _dateStr, instance) => {
            instance.calendarContainer.classList.add('tier-param-fp-cal');
        }
    };

    private formatLocalDateYmd(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    onParamDateValeurChange(row: ParamDataRow, value: unknown): void {
        if (value == null || value === '') {
            row.valeur = '';
            return;
        }
        if (Array.isArray(value) && value[0] instanceof Date) {
            row.valeur = this.formatLocalDateYmd(value[0]);
            return;
        }
        if (value instanceof Date) {
            row.valeur = this.formatLocalDateYmd(value);
            return;
        }
        const s = String(value).trim();
        row.valeur = s;
    }

    private buildFournisseurCoordinatePayloads(fournisseurId: number): CoordinateParamsPayload[] {
        const { stationId: sid, userId: uid } = this.getParamTablesApiIds();
        if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(uid) || uid <= 0) {
            return [];
        }
        if (!Number.isFinite(fournisseurId) || fournisseurId <= 0) {
            return [];
        }
        const out: CoordinateParamsPayload[] = [];
        for (const row of this.paramTableRows) {
            const ptid = row.param_table_id;
            if (ptid == null || ptid <= 0) {
                continue;
            }
            const v = (row.valeur ?? '').trim();
            if (v === '') {
                continue;
            }
            out.push({
                station_id: Math.floor(sid),
                user_id: Math.floor(uid),
                param_table_id: Math.floor(ptid),
                fournisseur_id: Math.floor(fournisseurId),
                valeur: v
            });
        }
        return out;
    }

    private saveFournisseurCoordinateParams$(fournisseurId: number): Observable<unknown | unknown[] | null> {
        const payloads = this.buildFournisseurCoordinatePayloads(fournisseurId);
        if (payloads.length === 0) {
            return of(null);
        }
        return this.paramTablesService.postCoordinateParams(payloads);
    }

    isValidPhoneNumber(num_tel: string | null): boolean {
        return !num_tel || (typeof num_tel === 'string' && /^[0-9]{8}$/.test(num_tel));
    }

    handleStepCompletion(stepIndex: number): void {
        if (stepIndex === 0) {
            this.submittedStep1 = true;
            this.validateStep1();
            if (!this.isStep1Valid) {
                return;
            }
            this.nextStep();
        } else if (stepIndex === 1) {
            this.validateStep2();
            this.validatePhoneVisual();
            this.validateAddressVisual();
            if (!this.isStep2Valid) {
                return;
            }
            this.resetStep2Errors();
            this.nextStep();
        } else if (stepIndex === 2) {
            this.validateParamStep();
            if (!this.isStepParamDataValid) {
                return;
            }
            this.saveFournisseur(false);
        } else if (stepIndex === 3) {
            this.router.navigate(['/bibliotheque-fournisseurs/actif']);
        }
    }

    saveFournisseur(autoNavigateToView: boolean = false): void {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        this.isLoading = true;
        this.isSaved = false;

        if (this.currentUserId <= 0 || this.currentStationId <= 0) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('ID utilisateur ou station invalide. Veuillez vous reconnecter.', 'Erreur');
            return;
        }

        if (!this.fournisseur.nom_entreprise || !this.fournisseur.civilite) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Veuillez remplir tous les champs requis : civilité et nom de l\'entreprise.', 'Erreur');
            return;
        }

        if (!this.validateStep3()) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Numéro de téléphone invalide. Veuillez entrer un numéro de 8 chiffres.', 'Erreur');
            return;
        }

        const fournisseurData: any = {
            id: this.fournisseurId || undefined,
            civilite: this.fournisseur.civilite,
            nom_entreprise: this.fournisseur.nom_entreprise,
            numero_identification_fiscale: this.fournisseur.numero_identification_fiscale || null,
            site_web: this.fournisseur.site_web || null,
            station_id: this.currentStationId,
            user_id: this.currentUserId,
            identifiant_unique: this.fournisseur.identifiant_unique || null,
            emails: this.fournisseur.emails
                .filter(email => email.email)
                .map(email => email.email),
            is_archive: this.fournisseur.is_archive || false,
            created_at: this.fournisseur.created_at || undefined,
            updated_at: new Date().toISOString(),
            telephones: this.fournisseur.telephones
                .filter(phone => phone.num_tel)
                .map(phone => ({
                    id: phone.id || undefined,
                    type: phone.type || null,
                    num_tel: phone.num_tel ? String(phone.num_tel) : null,
                    code_pays: phone.code_pays || null,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    fournisseur_id: this.fournisseurId || 0,
                    created_at: phone.created_at || new Date().toISOString(),
                    updated_at: new Date().toISOString()
                } as Telephone)),
            adresses: this.addressArray
                .filter(addr => addr.pays || addr.region || addr.code_postal || addr.adresse)
                .map(addr => ({
                    id: addr.id || undefined,
                    type: addr.type ? (addr.type === 'facturation' ? 'adresse_fac' : addr.type === 'livraison' ? 'adresse_li' : addr.type) : null,
                    pays: addr.pays ? (addr.pays === 'France' ? 'FR' : addr.pays === 'Belgique' ? 'BE' : addr.pays === 'Suisse' ? 'CH' : addr.pays === 'Maroc' ? 'MA' : addr.pays) : null,
                    region: addr.region || null,
                    code_postal: addr.code_postal || null,
                    adresse: addr.adresse || null,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    fournisseur_id: this.fournisseurId || 0,
                    created_at: addr.created_at || new Date().toISOString(),
                    updated_at: new Date().toISOString()
                } as Address))
        };

        const action = this.mode === 'edit' && this.fournisseurId !== null
            ? this.fournisseurService.updateFournisseur(this.fournisseurId, fournisseurData)
            : this.fournisseurService.addFournisseur(fournisseurData);

        action.pipe(
            switchMap((response: any) => {
                const apiFournisseurId = response.fournisseur?.id || response.fornisseur?.id || response.id || response.data?.id || response.fournisseur_id;

                if (apiFournisseurId && !isNaN(apiFournisseurId)) {
                    this.fournisseurId = apiFournisseurId;
                    this.fournisseur = { ...this.fournisseur, id: apiFournisseurId };
                } else if (this.mode === 'edit' && this.originalFournisseurId) {
                    this.fournisseurId = this.originalFournisseurId;
                } else {
                    this.toastr.warning('ID fournisseur non récupéré dans la réponse. Continuant sans ID.', 'Attention');
                }

                const fid = this.fournisseurId;
                if (fid == null || !Number.isFinite(Number(fid)) || Number(fid) <= 0) {
                    return of(response);
                }
                return this.saveFournisseurCoordinateParams$(Math.floor(Number(fid))).pipe(
                    map(() => response),
                    catchError((err) => {
                        this.toastr.warning(
                            err?.error?.message ||
                                'Les paramètres des données n\'ont pas pu être enregistrés.',
                            'Attention'
                        );
                        return of(response);
                    })
                );
            }),
            finalize(() => {
                this.isLoading = false;
                this.isSubmitting = false;
                this.cd.markForCheck();
            })
        ).subscribe({
            next: (response: any) => {
                this.isSaved = true;

                const fournisseurToPopulate = response.fournisseur || response.fornisseur || response.data || response;
                this.populateForm(fournisseurToPopulate as Fournisseur);

                this.toastr.success(`Fournisseur "${fournisseurData.nom_entreprise}" ${this.mode === 'edit' ? 'mis à jour' : 'créé'} avec succès !`, 'Succès');

                if (!autoNavigateToView) {
                    this.currentStep = this.steps.length - 1;
                    this.cd.markForCheck();
                }

                if (autoNavigateToView && this.fournisseurId) {
                    setTimeout(() => {
                        this.viewFournisseur(this.fournisseurId!);
                    }, 500);
                }
            },
            error: (error) => {
                this.isSaved = false;
                const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Données invalides. Veuillez vérifier les champs, notamment les numéros de téléphone.';
                this.toastr.error(errorMessage, 'Erreur');
                this.cd.markForCheck();
            }
        });
    }

    addAddress(): void {
        if (this.addressArray.length < 5) {
            this.addressArray = [...this.addressArray, {
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0
            }];
            this.hasAddedAddress = true;
            this.validateAddressVisual();
            this.validateStep2();
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        }
    }

    deleteAddress(index: number): void {
        this.addressArray = this.addressArray.filter((_, i) => i !== index);
        this.hasAddedAddress = this.addressArray.length > 0;
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    addPhone(): void {
        if (this.fournisseur.telephones.length < 5) {
            this.fournisseur.telephones = [...this.fournisseur.telephones, {
                type: null,
                num_tel: null,
                code_pays: '+216',
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: this.fournisseurId || 0
            } as Telephone];
            this.hasAddedPhone = true;
            this.validateStep3();
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        } else {
            this.toasterInfo('Vous ne pouvez pas ajouter plus de 5 numéros de téléphone.');
        }
    }

    deletePhone(index: number): void {
        this.fournisseur.telephones = this.fournisseur.telephones.filter((_, i) => i !== index);
        this.hasAddedPhone = this.fournisseur.telephones.length > 0;
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    addNewFournisseur(): void {
        this.currentStep = 0;
        this.mode = 'add';
        this.fournisseurId = null;
        this.originalFournisseurId = null;
        this.fournisseur = {
            civilite: '',
            nom_entreprise: '',
            numero_identification_fiscale: '',
            site_web: '',
            station_id: this.currentStationId,
            user_id: this.currentUserId,
            identifiant_unique: '',
            is_archive: false,
            emails: [{ email: '', user_id: this.currentUserId, fournisseur_id: 0, station_id: this.currentStationId }],
            telephones: [{
                type: null,
                num_tel: null,
                code_pays: '+216',
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: 0
            }],
            adresses: [{
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                fournisseur_id: 0
            }]
        };
        this.addressArray = this.fournisseur.adresses;
        this.hasAddedPhone = true;
        this.hasAddedAddress = true;
        this.isStep1Valid = false;
        this.isStep2Valid = false;
        this.isStep3Valid = true;
        this.isStepParamDataValid = true;
        this.paramTableRows = [];
        this.paramSelectOptionsCache.clear();
        this.headerService.setConfig({
            title: 'Ajouter Fournisseur',
            buttons: []
        });
        this.router.navigate(['/tiers/fournisseurs/add-fournisseur']);
        this.cd.detectChanges();
    }

    viewFournisseur(fournisseurId?: number): void {
        const effectiveId = fournisseurId || this.fournisseur?.id || this.fournisseurId || this.originalFournisseurId;

        if (!effectiveId || effectiveId <= 0) {
            this.toastr.error(`Navigation impossible : ID fournisseur manquant (${effectiveId}). Vérifiez les logs console.`, 'Erreur Critique');
            return;
        }

        this.router.navigate(['/tiers/fournisseurs/details', effectiveId, 'detail-contenue-apercue']);
    }

    quickAdd(): void {
        this.validateStep1();
        if (this.isStep1Valid && !this.isSubmitting) {
            this.saveFournisseur(false);
        } else {
            const missingFields: string[] = [];
            if (!this.fournisseur.nom_entreprise) missingFields.push("Nom de l'entreprise");
            if (!this.fournisseur.numero_identification_fiscale) missingFields.push("Numéro d'identification fiscale");
            if (!this.fournisseur.civilite) missingFields.push("Civilité");
            if (!this.fournisseur.identifiant_unique) missingFields.push("Nom du contact");

            const errorMessage = `Veuillez remplir les champs obligatoires : ${missingFields.join(', ')}.`;
            this.toastr.error(errorMessage, 'Erreur');
        }
    }

    toasterInfo(msg: any): void {
        this.toastr.info(msg, '', {
            positionClass: 'toast-bottom-right',
            toastClass: 'toast ngx-toastr',
            closeButton: true
        });
    }

    selectAddressType(index: number, type: string): void {
        this.addressArray[index].type = type;
        this.validateAddressVisual();
        this.validateStep2();
        this.refreshFeatherIcons();
    }

    clearFieldError(field: any): void {
        if (field && field.invalid) {
            field.reset(field.value);
        }
        this.submittedStep1 = false;
    }
    step2Errors: {
        phones: { [key: number]: {
                type?: string;
                code_pays?: string;
                number?: string;
            } };
        addresses: { [key: number]: {
                type?: string;
                pays?: string;
                region?: string;
                code_postal?: string;
                adresse?: string;
            } };
    } = { phones: {}, addresses: {} };

    hasPhoneData(index: number): boolean {
        const phone = this.fournisseur.telephones[index];
        return !!(phone?.type|| phone?.num_tel);
    }

    hasAddressData(index: number): boolean {
        const addr = this.addressArray[index];
        return !!(addr?.type || addr?.pays || addr?.region || addr?.code_postal || addr?.adresse);
    }
    clearPhone(index: number) {
        this.fournisseur.telephones[index] = {
            type: '',
            code_pays: '',
            num_tel: ''
        };
        this.validateStep3();
    }

    clearAddress(index: number) {
        this.addressArray[index] = {
            type: '',
            pays: '',
            region: '',
            code_postal: '',
            adresse: ''
        };
        this.validateStep3();
    }
    removeOrClearAddress(index: number): void {
        if (this.addressArray.length > 1) {
            this.deleteAddress(index);
        } else {
            this.clearAddress(index);
        }
        delete this.step2Errors.addresses[index]
        this.touchedAddressRows.delete(index);

        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }
    removeOrClearPhone(index: number): void {
        if (this.fournisseur.telephones.length > 1) {
            this.deletePhone(index);
        } else {
            this.clearPhone(index);
        }

        delete this.step2Errors.phones[index];
        this.touchedPhoneRows.delete(index);
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    private hasAnyPhoneFieldFilled(index: number): boolean {
        const phone = this.fournisseur.telephones[index];
        return !!(
            phone?.type ||
            phone?.num_tel ||
            (phone?.code_pays && phone.code_pays.trim() !== '')
        );
    }

    private hasAnyAddressFieldFilled(index: number): boolean {
        const addr = this.addressArray[index];
        return !!(
            addr?.type ||
            addr?.pays ||
            addr?.region ||
            addr?.code_postal ||
            addr?.adresse
        );
    }

    private setPhoneError(index: number, field: 'type' | 'number', message: string): void {
        if (!this.step2Errors.phones[index]) {
            this.step2Errors.phones[index] = {};
        }
        this.step2Errors.phones[index][field] = message;
    }

    private setAddressError(index: number, field: 'type' | 'pays' | 'region' | 'code_postal' | 'adresse', message: string): void {
        if (!this.step2Errors.addresses[index]) {
            this.step2Errors.addresses[index] = {};
        }
        this.step2Errors.addresses[index][field] = message;
    }

    validatePhoneVisual(): void {
        this.fournisseur.telephones.forEach((phone, index) => {
            const isTouched = this.hasAnyPhoneFieldFilled(index);

            this.step2Errors.phones[index] = {};

            if (isTouched) {
                if (!phone.type) {
                    this.step2Errors.phones[index].type = 'Le type de téléphone est obligatoire';
                }
                if (!phone.code_pays || phone.code_pays.trim() === '') {
                    this.step2Errors.phones[index].code_pays = 'Le code pays est obligatoire';
                }
                if (!phone.num_tel) {
                    this.step2Errors.phones[index].number = 'Le numéro est obligatoire';
                } else if (!/^[1-9][0-9]{7}$/.test(phone.num_tel.toString().trim())) {
                    this.step2Errors.phones[index].number = '8 chiffres requis (ex: 22123456)';
                }
            }
        });

        this.validateStep2();
        this.cd.detectChanges();
    }

    validateAddressVisual(): void {
        this.addressArray.forEach((addr, index) => {
            const isTouched = this.hasAnyAddressFieldFilled(index);

            this.step2Errors.addresses[index] = {};

            if (isTouched) {
                if (!addr.type) {
                    this.step2Errors.addresses[index].type = 'Le type d’adresse est obligatoire';
                }
                if (!addr.pays) {
                    this.step2Errors.addresses[index].pays = 'Le pays est obligatoire';
                }
                if (!addr.region) {
                    this.step2Errors.addresses[index].region = 'La région est obligatoire';
                }
                if (!addr.code_postal) {
                    this.step2Errors.addresses[index].code_postal = 'Le code postal est obligatoire';
                }
                if (!addr.adresse) {
                    this.step2Errors.addresses[index].adresse = 'L’adresse complète est obligatoire';
                }
            }
        });

        this.validateStep2();
        this.cd.detectChanges();
    }


}
